/**
 * Rev. Proc. 2003-68 safe-harbor spread per share (FMV × table factor; not strike-specific).
 * Mirrors the Buyer Equity Awards step in the client workflow.
 */
export function getRevProc200368SpreadPerShare(
  fmv: number,
  volatility: string | undefined,
  termMonths: number
): number {
  const termYears = Math.min(10, Math.max(1, Math.round(termMonths / 12)));
  const baseFactors: Record<string, Record<number, number>> = {
    low: { 1: 0.08, 5: 0.19, 10: 0.25 },
    medium: { 1: 0.15, 5: 0.34, 10: 0.45 },
    high: { 1: 0.23, 5: 0.47, 10: 0.59 },
  };
  const baseFactor = baseFactors[volatility || 'medium']?.[termYears] ?? 0.3;
  return fmv * baseFactor;
}

export type BuyerEquityAwardLike = {
  type?: string;
  shares?: string | number;
  fmvPerShare?: string | number;
  exercisePrice?: string | number;
  volatility?: string;
  remainingTermMonths?: string | number;
};

/** Parachute value for one buyer equity award (same rules as BuyerEquityAwards UI). */
export function calcBuyerEquityAwardParachuteValue(award: BuyerEquityAwardLike): number {
  const shares = Number(award.shares ?? 0) || 0;
  const fmv = Number(award.fmvPerShare ?? 0) || 0;
  if (award.type === 'option') {
    const exercisePrice = Number(award.exercisePrice ?? 0) || 0;
    if (exercisePrice <= 0 || fmv <= 0) return 0;
    const termMonths = Number(award.remainingTermMonths ?? 60) || 60;
    return shares * getRevProc200368SpreadPerShare(fmv, award.volatility, termMonths);
  }
  return shares * fmv;
}

/** Sum parachute values for all awards on one individual's buyer-equity record. */
export function sumBuyerEquityAwardsParachuteValue(awards: unknown[] | undefined): number {
  if (!awards?.length) return 0;
  return awards.reduce((sum: number, a) => sum + calcBuyerEquityAwardParachuteValue(a as BuyerEquityAwardLike), 0);
}
